import React, { useState } from 'react';

const defaultSettings = {
  sites: [
    { code: 'ST1998', name: 'Site 1998' },
    { code: 'ST2765', name: 'Site 2765' },
    { code: 'ST3865', name: 'Site 3865' },
    { code: 'ST4765', name: 'Site 4765' },
    { code: 'ST19098', name: 'Site 19098' },
    { code: 'ST2003', name: 'Site 2003' },
  ],
};

const SiteSettings = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [newSiteCode, setNewSiteCode] = useState('');
  const [newSiteName, setNewSiteName] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editSiteCode, setEditSiteCode] = useState('');
  const [editSiteName, setEditSiteName] = useState('');

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const addSite = () => {
    const code = newSiteCode.trim().toUpperCase();
    const name = newSiteName.trim();
    
    if (!code || !name) {
      alert('Please enter both Site Code and Site Name');
      return;
    }

    // Check if site code already exists
    if (settings.sites.some(site => site.code === code)) {
      alert('Site Code already exists');
      return;
    }

    update('sites', [...settings.sites, { code, name }]);
    setNewSiteCode('');
    setNewSiteName('');
  };

  const removeSite = (index) => {
    const newSites = settings.sites.filter((_, i) => i !== index);
    update('sites', newSites);
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditSiteCode(settings.sites[index].code);
    setEditSiteName(settings.sites[index].name);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditSiteCode('');
    setEditSiteName('');
  };

  const saveEdit = (index) => {
    const code = editSiteCode.trim().toUpperCase();
    const name = editSiteName.trim();
    
    if (!code || !name) {
      alert('Please enter both Site Code and Site Name');
      return;
    }

    // Check if site code already exists (excluding current index)
    if (settings.sites.some((site, i) => i !== index && site.code === code)) {
      alert('Site Code already exists');
      return;
    }

    const newSites = [...settings.sites];
    newSites[index] = { code, name };
    update('sites', newSites);
    cancelEdit();
  };

  const onReset = () => {
    setSettings(defaultSettings);
    setNewSiteCode('');
    setNewSiteName('');
    cancelEdit();
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Replace with API call to persist settings
      await new Promise((r) => setTimeout(r, 600));
      // eslint-disable-next-line no-console
      console.log('Site settings saved:', settings);
      alert('Site settings saved successfully!');
    } catch (e) {
      alert('Failed to save site settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Site Settings</h2>

      {/* Site Management */}
      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Manage Sites</h3>
        
        {/* Add New Site */}
        <div className="border-b pb-4 mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Add New Site</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Site Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newSiteCode}
                onChange={(e) => setNewSiteCode(e.target.value.toUpperCase())}
                placeholder="e.g., ST1998"
                className="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addSite();
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Site Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                placeholder="e.g., Site 1998"
                className="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addSite();
                  }
                }}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={addSite}
                className="w-full px-4 py-2 rounded-md border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                Add Site
              </button>
            </div>
          </div>
        </div>

        {/* Sites List */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Existing Sites</h4>
          {settings.sites.length === 0 ? (
            <p className="text-sm text-gray-500">No sites added yet.</p>
          ) : (
            <div className="space-y-2">
              {settings.sites.map((site, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-3 border rounded-md hover:bg-gray-50"
                >
                  {editingIndex === index ? (
                    <>
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Site Code</label>
                          <input
                            type="text"
                            value={editSiteCode}
                            onChange={(e) => setEditSiteCode(e.target.value.toUpperCase())}
                            className="w-full rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Site Name</label>
                          <input
                            type="text"
                            value={editSiteName}
                            onChange={(e) => setEditSiteName(e.target.value)}
                            className="w-full rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveEdit(index)}
                          className="px-3 py-1 text-sm rounded-md bg-primary text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="px-3 py-1 text-sm rounded-md border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-xs text-gray-600">Site Code:</span>
                          <span className="ml-2 font-medium text-gray-900">{site.code}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600">Site Name:</span>
                          <span className="ml-2 font-medium text-gray-900">{site.name}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(index)}
                          className="px-3 py-1 text-sm rounded-md border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                          title="Edit"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => removeSite(index)}
                          className="px-3 py-1 text-sm rounded-md border hover:bg-red-50 text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                          title="Delete"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          className={`px-4 py-2 rounded-md text-white ${saving ? 'bg-primary/70' : 'bg-primary hover:opacity-90'}`}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <button
          onClick={onReset}
          disabled={saving}
          className="px-4 py-2 rounded-md border hover:bg-gray-50"
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
};

export default SiteSettings;

